import React, { useMemo, useState } from 'react';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import { useHistory } from 'react-router-dom';

import cn from 'classnames';

import { Select } from '../index';

import { config } from '../../api/config';
import OrdersReviewChart from '../ordersReviewChart/ordersReviewChart';
import DeviceUptimeCharts from '../deviceUptimeCharts/deviceUptimeCharts';
import DeviceUptimeChartsByPeriod from '../deviceUptimeCharts/deviceUptimeChartsByPeriod';
import Integrations from '../Intergrations/integrations';
import MonitoringErrorMetrics from '../monitoringErrorMetrics/monitoringErrorMetrics';
import { buildFailedOrdersPreset } from '../../pages/orders/orders.helpers';

import { OrderChartViews } from '../../utils/global/enums';
import { centsToMoney } from '../../utils/global/helpers';
import { mapServiceTypes, serviceTypes } from '../../utils';

import styles from './monitoringContent.module.scss';
import TabGroup from '../TabGroup/TabGroup';

const serviceOptions = [
  { name: 'Paslaugos', value: '' },
  ...serviceTypes.map(type => ({
    name: mapServiceTypes(type),
    value: String(type),
  })),
];

const MonitoringContent = ({
  monitoring: { monitoring },
  serviceId,
  setServiceId,
}) => {
  const history = useHistory();
  const [viewMode, setViewMode] = useState(OrderChartViews.ORDERS);

  const onViewModeChange = value => () => {
    setViewMode(value);
  };

  const chartData = useMemo(() => {
    if (monitoring.current_period && monitoring.previous_year_period) {
      return monitoring.current_period.map(item => {
        const combinedOrders =
          item.successful_order_count + item.unsuccessful_order_count;
        const combinedRevenue =
          centsToMoney(item.successful_revenue) +
          centsToMoney(item.unsuccessful_revenue);

        const previousPeriod = monitoring.previous_year_period.find(
          previousData => {
            const current_date = new Date(item.date);
            const current_month = current_date.getMonth() + 1;
            const current_day = current_date.getDate();

            const previous_date = new Date(previousData.date);
            const previous_month = previous_date.getMonth() + 1;
            const previous_day = previous_date.getDate();

            return (
              current_month === previous_month && current_day === previous_day
            );
          }
        );

        const lastResultOrders = previousPeriod
          ? previousPeriod.successful_order_count +
            previousPeriod.unsuccessful_order_count
          : 0;

        const lastResultRevenue = previousPeriod
          ? previousPeriod.successful_revenue +
            previousPeriod.unsuccessful_revenue
          : 0;

        return {
          date: item.date,
          successful_order_count: item.successful_order_count,
          unsuccessful_order_count: item.unsuccessful_order_count,
          successful_revenue: centsToMoney(item.successful_revenue),
          unsuccessful_revenue: centsToMoney(item.unsuccessful_revenue),
          combinedOrders,
          combinedRevenue,
          lastResultOrders,
          lastResultRevenue: centsToMoney(lastResultRevenue),
        };
      });
    }
    return [];
  }, [monitoring]);

  const viewModeTabItems = [
    {
      key: OrderChartViews.ORDERS,
      label: 'Užsakymai',
      isActive: viewMode === OrderChartViews.ORDERS,
      onClick: onViewModeChange(OrderChartViews.ORDERS),
    },
    {
      key: OrderChartViews.REVENUE,
      label: 'Apyvarta',
      isActive: viewMode === OrderChartViews.REVENUE,
      onClick: onViewModeChange(OrderChartViews.REVENUE),
    },
  ];

  const openFailedOrders = chartItem => {
    if (
      viewMode !== OrderChartViews.ORDERS ||
      !chartItem ||
      !chartItem.date ||
      !chartItem.unsuccessful_order_count
    ) {
      return;
    }

    const presetOrderFilters = buildFailedOrdersPreset({
      date: chartItem.date,
      serviceId,
    });

    if (!presetOrderFilters) {
      return;
    }

    history.push({
      pathname: config.APP_PATHS.orders,
      state: { presetOrderFilters },
    });
  };

  return (
    <>
      <div className={styles.chartContainer}>
        <div className={styles.chartHeader}>
          <div className={styles.chartTitleWrapper}>
            <h2 className={styles.chartHeaderTitle}>
              {viewMode === OrderChartViews.ORDERS
                ? 'Užsakymų peržiūra'
                : 'Apyvartos peržiūra'}
            </h2>
            <span className={styles.chartHeaderSubtitle}>
              {viewMode === OrderChartViews.ORDERS
                ? 'Peržiūra lyginant su praeitais metais'
                : 'Apyvartos peržiūra lyginant su praeitais metais'}
            </span>
          </div>
          <div className={styles.chartActions}>
            <Select
              name="service"
              options={serviceOptions}
              selectedValue={serviceId}
              onChange={event => {
                setServiceId(event.target.value);
              }}
              className={styles.serviceSelect}
            />
            <TabGroup items={viewModeTabItems} />
          </div>
        </div>
        <OrdersReviewChart
          data={chartData}
          viewMode={viewMode}
          onUnsuccessfulBarClick={openFailedOrders}
        />
      </div>
      <div className={styles.deviceUptimeChartContent}>
        <DeviceUptimeCharts />
        <DeviceUptimeChartsByPeriod />
        <div className={styles.integrationsWrapper}>
          <Integrations />
        </div>
      </div>
      <MonitoringErrorMetrics />
    </>
  );
};

const mapStateToProps = state => ({
  monitoring: state.monitoring,
});

MonitoringContent.propTypes = {
  monitoring: PropTypes.shape({
    loading: PropTypes.bool.isRequired,
    monitoring: PropTypes.shape({
      current_period: PropTypes.arrayOf(
        PropTypes.shape({
          date: PropTypes.string,
          successful_order_count: PropTypes.number,
          successful_revenue: PropTypes.number,
          unsuccessful_order_count: PropTypes.number,
          unsuccessful_revenue: PropTypes.number,
        })
      ),
      previous_year_period: PropTypes.arrayOf(
        PropTypes.shape({
          date: PropTypes.string,
          successful_order_count: PropTypes.number,
          successful_revenue: PropTypes.number,
          unsuccessful_order_count: PropTypes.number,
          unsuccessful_revenue: PropTypes.number,
        })
      ),
    }),
  }).isRequired,
  serviceId: PropTypes.string,
  setServiceId: PropTypes.func,
};

export default connect(mapStateToProps)(MonitoringContent);
